import Logger from '@/common/utils/Logger';
import {
    openKboardConnection,
    closeKboardConnection,
    getConnectionStatus,
    isKboardTabOpen,
    sendSheetKnowledgeToKboard,
    sendUpdatedThreadIdToKboard,
} from '@/service/kboardConnectService';
import KboardConstant from '@/common/constant/KboardConstant';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { getPublicAPI } from '@/service/publicService';
import { createNewTicketAPI, editTicketData, getTicketById } from '@/service/ticketService';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { Storage } from 'aws-amplify';
import axios from 'axios';
import {
    handleGetAIBodyStringContent,
    initTicketKnowledgePrompt,
} from '@/component/common/modal/AIPrompt/HanlderAIPrompt';
import { sendAIChatMessage } from '@/service/chatAIService';
import { updateTabNameAfterSendMessage } from '@/component/chatAI/handler/ChatAIHandler';
import { v4 as uuidv4 } from 'uuid';
import { isDisplaySplitPDF } from '@/component/formManagement/handler/FormListHandler';
import { getRecoil, setRecoil } from 'recoil-nexus';
import { multiPanelAtom } from '@/state/atom/panelAtom';
import { kboardPendingResultAtom } from '@/state/atom/kboardAtom';
import { userAtom } from '@/state/atom/userAtom';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import { userPermissionToViewKnowledgeAtom } from '@/state/atom/userAtom';
import { setPanelSelector, updatePanelSelector } from '@/state/selector/panelSelector';
import { isLimitedTabsLength } from '@/service/userService';
import { handleOpenPanelOpenChatAI } from '@/component/common/layout/gNav/handler/MenuHandler';
import OpenAiUtil from '@/common/utils/OpenAiUtil';
import moment from 'moment';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

// === SSRF Protection: Whitelist allowed KBoard domains ===
const KBOARD_ALLOWED_DOMAINS = [
    process.env.REACT_APP_KBOARD_CDN_DOMAIN,
    process.env.REACT_APP_KBOARD_DOMAIN,
    Constant.KBOARD_CONNECT?.DOMAIN,
].filter(Boolean).map(d => {
    try { return new URL(d).hostname; }
    catch (e) { return d.replace(/^https?:\/\//, '').split('/')[0]; }
});

/**
 * Validate URL only allows fetch from trusted KBoard domains.
 * Blocks SSRF: private IPs, localhost, non-HTTPS, unknown domains.
 *
 * @param {string} url - URL to validate
 * @returns {boolean} true if URL is safe to fetch
 */
const isAllowedKboardUrl = (url) => {
    if (!url || typeof url !== 'string') return false;

    try {
        const parsed = new URL(url);

        // Only allow HTTPS
        if (parsed.protocol !== 'https:') return false;

        // Block private/internal IPs
        const hostname = parsed.hostname;
        if (
            hostname === 'localhost' ||
            hostname === '127.0.0.1' ||
            hostname.startsWith('10.') ||
            hostname.startsWith('192.168.') ||
            hostname.startsWith('172.') ||
            hostname === '0.0.0.0' ||
            hostname.endsWith('.internal') ||
            hostname.endsWith('.local')
        ) {
            return false;
        }

        // Check whitelist - allow if hostname matches or is subdomain of allowed domain
        if (KBOARD_ALLOWED_DOMAINS.length === 0) return true; // No whitelist configured = allow all HTTPS
        const isWhitelisted = KBOARD_ALLOWED_DOMAINS.some(domain =>
            hostname === domain || hostname.endsWith('.' + domain)
        );

        return isWhitelisted;
    } catch (e) {
        return false;
    }
};

const getPromptPlainBody = prompt => {
    const body = prompt?.settingAI?.AIPromptBody;
    if (Utils.isEmptyString(body)) return '';

    return handleGetAIBodyStringContent(body)
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');
};

const getPromptSortTime = prompt => {
    const time = new Date(prompt?.createdAt || prompt?.updatedAt || 0).getTime();
    return Number.isNaN(time) ? 0 : time;
};

const dedupePromptById = listPrompt => {
    const promptById = new Map();

    listPrompt.forEach(prompt => {
        if (!prompt?.id || promptById.has(prompt.id)) return;
        promptById.set(prompt.id, prompt);
    });

    return Array.from(promptById.values());
};

const sortKboardPrompt = (firstPrompt, secondPrompt) => {
    const diffTime = getPromptSortTime(secondPrompt) - getPromptSortTime(firstPrompt);
    if (diffTime !== 0) return diffTime;

    return `${secondPrompt?.id || ''}`.localeCompare(`${firstPrompt?.id || ''}`);
};

const getPromptSetting = (prompt, sysField, fallbackKey) => {
    return prompt?.settingAI?.[sysField] || prompt?.settingAI?.[fallbackKey] || '';
};

const buildKboardPromptKnowledgeList = async ({
    API,
    corporationDetail,
    projectInuseDetail,
    currentUserDetail,
}) => {
    try {
        const corporationId = projectInuseDetail?.affiliationOrganization || corporationDetail?.id || '';
        const projectId = projectInuseDetail?.id || '';
        if (Utils.isEmptyString(projectId)) return [];

        const data = await initTicketKnowledgePrompt(
            API,
            corporationId,
            projectId,
            corporationDetail?.id,
            {
                userId: currentUserDetail?.id,
                promptDefinitionVariant: Constant.FORM_VARIANT.KBOARD_PROMPT,
                sortBy: Constant.PROMPT_SORT_BY.CREATED_AT,
            }
        );
        if (data?.isUsingKnowledgeService === false) return [];

        const listPrompt = data?.listPrompt || [];

        return dedupePromptById(listPrompt)
            .filter(prompt => {
                const applicationTarget = prompt?.settingAI?.AIPromptApplicationTarget;
                return (
                    !Utils.isEmptyArray(applicationTarget) &&
                    applicationTarget.includes(Constant.AI_SYSTEM_FIELD_TARGET_OPTION_TAGS.INTERNAL)
                );
            })
            .sort(sortKboardPrompt)
            .map(prompt => ({
                id: prompt.id,
                title: prompt.title,
                promptBody: getPromptPlainBody(prompt),
                createdTime: prompt.createdAt || prompt.updatedAt || '',
                chatGptModel: getPromptSetting(
                    prompt,
                    Constant.AI_AUTO.SYSTEM_FIELD.CHATGPT_MODEL,
                    'field_chatgpt_model_ai_auto'
                ),
                reasoningEffort: getPromptSetting(
                    prompt,
                    Constant.AI_AUTO.SYSTEM_FIELD.REASONING_EFFORT,
                    'field_reasoning_effort_ai_auto'
                ),
            }));
    } catch (error) {
        console.log('[KBoardConnectHandler] Failed to load KBoard prompt knowledge:', error);
        return [];
    }
};

/**
 * Mock handler: resolve knowledge info for a given sheet.
 * TODO: Replace with real API call once backend provides sheet knowledge lookup.
 *
 * @param {Object} params
 * @param {string|number} params.sheetId - The sheet ID requested by kboard
 * @param {string} params.sheetName - The sheet name
 * @param {string} params.projectId - The sdesk project ID, used to scope the knowledge lookup
 * @param {string} params.corporationId - The sdesk corporation ID, used to scope the knowledge lookup
 * @returns {{ knowledgeMeta: string, knowledgeUrl: string }}
 */
const resolveSheetKnowledge = async ({ sheetId, sheetName, projectId, corporationId } = {}) => {
    const kboardDomain =
        process.env.REACT_APP_KBOARD_DOMAIN || Constant.KBOARD_CONNECT?.DOMAIN || '';
    let sheetUrl = `${kboardDomain}/sheet/${sheetId}`;
    const checkExistResult = await checkKBKnowledgeExists(projectId, sheetUrl, corporationId);
    if (checkExistResult.exists) {
        let shareLink = checkExistResult.shareLink;
        let ticketAllData = checkExistResult.ticketAllData;
        let ticketMainRecord = ticketAllData?.find(
            ticketRecord => ticketRecord.dataType === 'TICKET'
        );
        let updatedAt = ticketMainRecord?.updatedAt;
        updatedAt = !Utils.isEmptyString(updatedAt) ? Utils.getDateTimeByFormatJapanYYYY_MM_DDHHMM(updatedAt) : updatedAt;
        return {
            knowledgeMeta: updatedAt,
            knowledgeUrl: shareLink
        };
    }

    // Mock data — replace with real API once backend is ready
    return {
        knowledgeMeta: '',
        knowledgeUrl: ''
    }

};

/**
 * Handler for KBoard Connect feature in InputChatAiGroupButtons.
 * Manages the click action and result handling for the KBoard Connect button.
 */

/**
 * Handle click on the KBoard Connect button.
 * Opens kboard in a new tab and establishes cross-tab communication.
 *
 * @param {Object} params
 * @param {Object} params.API - Amplify API client
 * @param {Object} params.corporationDetail - Current corporation info (contains org id/name)
 * @param {Object} params.projectInuseDetail - Current project info (contains project id/name)
 * @param {Object} params.currentUserDetail - Current user info
 * @param {Function} params.onKboardResult - Callback when kboard sends result data back
 * @param {Function} params.onStatusChange - Callback when connection status changes
 * @returns {boolean} Whether the connection attempt was successful
 */
export const handleConnectKboard = async ({
    API,
    corporationDetail,
    projectInuseDetail,
    currentUserDetail,
    aiThreadChatId,
    onKboardResult,
    onStatusChange,
    handleConnectData
}) => {
    if (!corporationDetail || !projectInuseDetail) {
        return false;
    }

    const organizationDetail = {
        id: corporationDetail?.id || '',
        name: corporationDetail?.corporationName || '',
        limitValue: {
            kBoard: corporationDetail?.limitValue?.kBoard || Constant.STATUS_SERVICE_PERMISSION.OFF,
            usingKBoard:
                corporationDetail?.limitValue?.usingKBoard || Constant.STATUS_SERVICE_PERMISSION.OFF
        }
    };

    const projectDetail = {
        id: projectInuseDetail?.id || '',
        name: projectInuseDetail?.name || ''
    };

    const promptKnowledgeList = await buildKboardPromptKnowledgeList({
            API,
            corporationDetail,
            projectInuseDetail,
            currentUserDetail,
        });

    const success = openKboardConnection({
        organizationDetail,
        projectDetail,
        promptKnowledgeList,
        aiThreadChatId,
        onResult: (payload) => {
            onKboardResult?.({payload, handleConnectData});
        },
        onStatusChange: (status) => {
            onStatusChange?.(status);
        },
        onSheetKnowledgeRequest: (payload) => {
            return resolveSheetKnowledge(payload);
        },
    });

    return success;
};

/**
 * Handle disconnect from kboard.
 */
export const handleDisconnectKboard = () => {
    closeKboardConnection();
};

/**
 * Check if kboard is currently connected.
 * @returns {boolean}
 */
export const isKboardConnected = () => {
    return getConnectionStatus() === KboardConstant.CONNECTION_STATUS.CONNECTED;
};

/**
 * Check if kboard tab is currently open (may or may not be fully connected).
 * @returns {boolean}
 */
export const isKboardOpen = () => {
    return isKboardTabOpen();
};

/**
 * Check conditions to show KBoard Connect button.
 * Verifies KBoard is enabled for the corporation AND the selected knowledge project
 * matches the current project in use (or no specific project is selected).
 *
 * @param {Object} params
 * @param {Object} params.corporationDetail - Current corporation detail (must have limitValue with kBoard/usingKBoard settings)
 * @param {string} params.projectIdKnowledgeSelected - Project ID currently selected in the Knowledge list filter
 * @param {Object} params.projectInuseDetail - Current project in use (contains id)
 * @returns {boolean} true if KBoard Connect button should be displayed
 */
export const isShowKBoardConnect = ({
    corporationDetail,
    projectIdKnowledgeSelected,
    projectInuseDetail
}) => {
    if (!corporationDetail) return false;
    if (!Utils.isEnableUsingKBoard(corporationDetail)) return false;
    return projectIdKnowledgeSelected === projectInuseDetail?.id;
};

/**
 * Download a single file from CloudFront URL and upload to S3 tmp folder.
 * Logic referenced from backend downloadFileFromUrl (ValidateContentData.js).
 *
 * @param {string} fileUrl - CloudFront URL
 * @param {Object} options - { corporationId, uploadTime }
 * @returns {Promise<Object>} fileInfo object
 */
const downloadSingleFileFromUrl = async (fileUrl, { corporationId, uploadTime }, attachmentFiles) => {
    // SSRF Protection: validate URL origin before fetching
    if (!isAllowedKboardUrl(fileUrl)) {
        throw new Error(`[KBoardConnectHandler] Blocked: URL not in allowed KBoard domains: ${fileUrl}`);
    }

    const response = await axios.get(fileUrl, {
        responseType: 'arraybuffer',
        timeout: 30000,
        maxContentLength: 10 * 1024 * 1024,
        validateStatus: (status) => status < 400
    });

    let extension = Utils.getFileExtension(fileUrl);
    extension = extension?.toLowerCase();
    let mimeType = '';
    if (Constant.ATTACH_FILE_IMAGE_LIST_EXTENSIONS?.includes(extension)) {
        mimeType = `image/${extension}`;
    } else {
        mimeType = response.headers['content-type'] || '';
        if (mimeType.includes(';')) {
            mimeType = mimeType.split(';')[0].trim();
        }
        if (!mimeType) {
            mimeType = 'application/octet-stream';
        }
    }

    // 2. Extract filename (content-disposition > URL path > fallback)
    let fileName = '';
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
            fileName = decodeURIComponent(match[1].replace(/['"]/g, ''));
        }
    }
    if (!fileName) {
        try {
            const urlObj = new URL(fileUrl);
            const pathParts = urlObj.pathname.split('/');
            fileName = decodeURIComponent(pathParts[pathParts.length - 1]) || '';
        } catch (e) {
            // ignore
        }
    }
    if (!fileName) {
        const ext = mimeType.split('/')[1] || 'bin';
        fileName = `file-${Date.now()}.${ext}`;
    }

    // 3. Convert arraybuffer to Blob then File for Storage.put
    const blob = new Blob([response.data], { type: mimeType });
    const file = new File([blob], fileName, { type: mimeType });

    // 4. Upload to S3 tmp folder
    const s3Key = `tmp/${corporationId}/${uploadTime}/${fileName}`;
    const uploadResult = await Storage.put(s3Key, file, {
        contentType: mimeType,
        level: 'public',
        contentDisposition: `inline; filename=${encodeURIComponent(fileName)}`,
        acl: 'public-read'
    });

    if (!uploadResult) {
        throw new Error('S3 upload failed');
    }

    // 5. Return file info matching uploadTicketFileToS3 format
    const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
    let originUrlInfo = attachmentFiles?.find(item => item.fileUri === fileUrl);
    let kboardUploadedAt = '';
    if (originUrlInfo?.updatedAt) {
        kboardUploadedAt = new Date(originUrlInfo?.updatedAt);
    }
    return {
        fileUrl: s3Key,
        fileSize: String(blob.size),
        fileName: fileName,
        isPdf: fileExtension === 'pdf',
        splitPdf: false,
        createdAt: new Date().toISOString(),
        originUrl: fileUrl,
        kboardUploadedAt: kboardUploadedAt
    };
};

/**
 * Download files from KBoard CloudFront URLs and upload to S3 tmp folder.
 * Output format matches uploadTicketFileToS3 for direct use in createTicketRequestDto.
 *
 * @param {Array<string>} fileUrls - List of CloudFront file URLs from KBoard
 * @param {Object} options - { corporationId }
 * @returns {Promise<{ fileListSuccess: Array, suffixOfAttachments: Object, listFileError: Array }>}
 */
export const downloadAndUploadFilesFromUrls = async (fileUrls, corporationId, attachmentFiles) => {
    const fileListSuccess = [];
    const suffixOfAttachments = {};
    const listFileError = [];

    const validUrls = (fileUrls || []).filter(url => url && typeof url === 'string');
    if (validUrls.length === 0) {
        return { fileListSuccess, suffixOfAttachments, listFileError };
    }

    const uploadTime = new Date().getTime();

    const results = await Promise.allSettled(
        validUrls.map((url, index) => downloadSingleFileFromUrl(url, { corporationId, uploadTime: uploadTime + index }, attachmentFiles))
    );

    results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
            const fileInfo = result.value;
            fileListSuccess.push(fileInfo);

            const ext = fileInfo.fileName.split('.').pop()?.toLowerCase() || '';
            if (ext) {
                suffixOfAttachments[ext] = (suffixOfAttachments[ext] || 0) + 1;
            }
        } else {
            listFileError.push(validUrls[index]);
        }
    });

    return { fileListSuccess, suffixOfAttachments, listFileError };
};

/**
 * Upload KBoard screen capture image to S3.
 * Priority: selectionImageUrl > sheetImageUrl
 * S3 key format: {corporationId}/{projectId}/chatThread/{chatThreadId}/chatpostImage/kboard-{timestamp}/{fileName}
 *
 * @param {Object} payload - KBoard payload containing selectionImageUrl / sheetImageUrl
 * @param {Object} context - { corporationId, projectId, chatThreadId }
 * @returns {Promise<string>} S3 key of uploaded image, or empty string if no image
 */
export const uploadSnapshotXML = async (payload, { corporationId, projectId, ticketId }) => {
    const imageUrl = payload?.sheetImageUrl || '';
    if (!imageUrl) return { s3Key: '', localUrl: '' };

    // SSRF Protection: validate URL origin before fetching
    if (!isAllowedKboardUrl(imageUrl)) {
        return { s3Key: '', localUrl: '' };
    }

    try {
        const response = await axios.get(imageUrl, {
            responseType: 'arraybuffer',
            timeout: 30000,
            maxContentLength: 5 * 1024 * 1024,
            validateStatus: (status) => status < 400
        });

        let mimeType = 'image/png';

        // Extract filename from URL or generate one
        let fileName = 'kboardXmlSnapshot';

        const s3Key = `${corporationId}/${projectId}/ticket/${ticketId}/image/kboardXmlSnapshot`;

        const blob = new Blob([response.data], { type: mimeType });
        const file = new File([blob], fileName, { type: mimeType });
        await Storage.put(s3Key, file, {
            contentType: mimeType,
            level: 'public',
            cacheControl: 'no-cache',
            contentDisposition: 'inline',
        });

        return { s3Key: `public/${s3Key}` };
    } catch (error) {
        console.log('[KBoardConnectHandler] Failed to upload snapshot xml :', error);
        return { s3Key: '', localUrl: '' };
    }
};


/**
 * Upload KBoard screen capture image to S3.
 * Priority: selectionImageUrl > sheetImageUrl
 * S3 key format: {corporationId}/{projectId}/chatThread/{chatThreadId}/chatpostImage/kboard-{timestamp}/{fileName}
 *
 * @param {Object} payload - KBoard payload containing selectionImageUrl / sheetImageUrl
 * @param {Object} context - { corporationId, projectId, chatThreadId }
 * @returns {Promise<string>} S3 key of uploaded image, or empty string if no image
 */
export const uploadKboardScreenCapture = async (payload, { corporationId, projectId, chatThreadId }) => {
    const imageUrl = payload?.selectionImageUrl || payload?.sheetImageUrl || '';
    if (!imageUrl) return { s3Key: '', localUrl: '' };

    // SSRF Protection: validate URL origin before fetching
    if (!isAllowedKboardUrl(imageUrl)) {
        return { s3Key: '', localUrl: '' };
    }

    try {
        const response = await axios.get(imageUrl, {
            responseType: 'arraybuffer',
            timeout: 30000,
            maxContentLength: 5 * 1024 * 1024,
            validateStatus: (status) => status < 400
        });

        let mimeType = response.headers['content-type'] || 'image/png';
        if (mimeType.includes(';')) {
            mimeType = mimeType.split(';')[0].trim();
        }

        // Extract filename from URL or generate one
        let fileName = '';
        try {
            const urlObj = new URL(imageUrl);
            const pathParts = urlObj.pathname.split('/');
            fileName = decodeURIComponent(pathParts[pathParts.length - 1]) || '';
        } catch (e) {
            // ignore
        }
        if (!fileName) {
            const ext = mimeType.split('/')[1] || 'png';
            fileName = `kboard-capture-${Date.now()}.${ext}`;
        }

        const timestamp = Date.now();
        const s3Key = `${corporationId}/${projectId}/chatThread/${chatThreadId}/chatpostImage/kboard-${timestamp}/${fileName}`;

        const blob = new Blob([response.data], { type: mimeType });
        const file = new File([blob], fileName, { type: mimeType });

        // Create temporary local URL for immediate display
        const localUrl = URL.createObjectURL(blob);
        await Storage.put(s3Key, file, {
            contentType: mimeType,
            level: 'public',
        });

        return { s3Key: `public/${s3Key}`, localUrl };
    } catch (error) {
        console.log('[KBoardConnectHandler] Failed to upload screen capture:', error);
        return { s3Key: '', localUrl: '' };
    }
};

const sendTicketInfoToKboard = async (ticketId, sheetId) => {
    try {
        const newTicketDetail = await getTicketById(null, ticketId);
        if (!newTicketDetail) {
            return;
        }
        const dataShareLink = Utils.convertJSONDataToObject(newTicketDetail?.dataShareLink);
        const { shortKey, type, generateLinkCorporationDomain } = dataShareLink || {};
        const linkUrl = `${window.location.origin}/${Utils.getUrlShortShareLinkTicket({
            type,
            shortKey,
            subDomain: generateLinkCorporationDomain
        })}`;
        let updatedAt = newTicketDetail?.updatedAt;
        updatedAt = !Utils.isEmptyString(updatedAt) ? Utils.getDateTimeByFormatJapanYYYY_MM_DDHHMM(updatedAt) : updatedAt;
        sendSheetKnowledgeToKboard({ sheetId, knowledgeMeta: updatedAt, knowledgeUrl: linkUrl });
    } catch (error) {
        console.log(error)
    }
};

/**
 * Process payload from kboard and build partial createTicketRequestDto for KBOARD_PROMPT knowledge ticket.
 * Caller must add context fields: corporationOrigin, createUserId, projectOrigin, projectTarget,
 * affiliationOrganization, affiliationProject, status.
 *
 * @param {Object} payload - Data received from kboard via postMessage
 * @param {Array} listFormDetail - List of forms in current project
 * @returns {Object|null} Partial createTicketRequestDto or null if KBOARD_PROMPT form not found
 */
export const handleDataFromKboard = async (payload, handleConnectData) => {
    try {
        let listFormDetail = handleConnectData?.formList;
        let listFieldDetail = handleConnectData?.fieldList;
        let corporationDetail = handleConnectData?.corporationDetail;
        let sdeskContext = payload?.sdeskContext;
        let sheetInfo = payload?.sheetInfo;
        let { organizationId, projectId } = sdeskContext;

        if (!payload || Utils.isEmptyArray(listFormDetail)) {
            return null;
        }

        // Find the KBOARD_PROMPT form
        const kboardKnowledgeForm = listFormDetail.find((form) =>
            Utils.isSystemKBoardKnowledgeForm(form),
        );
        if (!kboardKnowledgeForm) {
            return null;
        }

        // Parse form structure to map position → field ID
        const formStructure =
            typeof kboardKnowledgeForm.structure === 'string'
                ? JSON.parse(kboardKnowledgeForm.structure)
                : kboardKnowledgeForm.structure;

        if (!formStructure) {
            return null;
        }

        // KB Knowledge form fields - find by sysField using constants
        const sheetNameField = listFieldDetail?.find(
            (field) => field.sysField === Constant.SYSTEM_FIELD.KB_SHEET_NAME,
        );
        const sheetIdField = listFieldDetail?.find(
            (field) => field.sysField === Constant.SYSTEM_FIELD.KB_SHEET_ID,
        );
        const kbXmlField = listFieldDetail?.find(
            (field) => field.sysField === Constant.SYSTEM_FIELD.KB_XML,
        );
        const kbTimestampField = listFieldDetail?.find(
            (field) => field.sysField === Constant.SYSTEM_FIELD.KB_XML_TIMESTAMP,
        );

        // Build fields data for KB Knowledge ticket
        const customFields = {};

        // Sheet Name (TEXT)
        if (sheetNameField) {
            customFields[sheetNameField.id] = !Utils.isEmptyString(sheetInfo?.sheetName) ? sheetInfo?.sheetName : '';
        };

        // Sheet ID (URL)
        if (sheetInfo.sheetId && sheetIdField) {
            const kboardDomain =
                process.env.REACT_APP_KBOARD_DOMAIN || Constant.KBOARD_CONNECT?.DOMAIN || '';
            let sheetUrl = `${kboardDomain}/sheet/${sheetInfo.sheetId}`;
            customFields[sheetIdField.id] = sheetUrl;
        }

        // KB-XML Content (TEXT)
        if (payload?.sheetXmlData?.xml && kbXmlField) {
            customFields[kbXmlField.id] = payload?.sheetXmlData?.xml;
        }

        // KB-XML Timestamp (DATE)
        if (sheetInfo.sheetUpdatedAt && kbTimestampField) {
            const m = moment(sheetInfo.sheetUpdatedAt);
            customFields[kbTimestampField.id] = m.isValid()
                ? m.format(Constant.MOMENT_DATE_FORMAT.YYYYMMDD_HHMM)
                : sheetInfo.sheetUpdatedAt;
        }

        if (sheetIdField && customFields[sheetIdField.id]) {
            const checkExistResult = await checkKBKnowledgeExists(
                projectId,
                customFields[sheetIdField.id],
                organizationId,
            );

            if (checkExistResult?.exists) {
                // Ticket already exists → update it
                const updateResult = await updateExistingKBKnowledge({
                    ticketId: checkExistResult.ticketId,
                    ticketAllData: checkExistResult.ticketAllData,
                    customFields,
                    payload,
                    organizationId,
                    sdeskContext,
                    kboardKnowledgeForm,
                    kbTimestampField,
                    userMail: handleConnectData?.userMail || '',
                    corporationDetail,
                });

                await sendTicketInfoToKboard(checkExistResult.ticketId, sheetInfo.sheetId);

                // Send AI Chat if payload contains aiPrompt
                if (payload?.aiPrompt?.message) {
                    await sendAIChatFromKboard({
                        payload,
                        handleConnectData,
                        knowledgeTicketId: checkExistResult.ticketId,
                        sdeskContext,
                    });
                }

                return updateResult;
            } else if (!checkExistResult.success) {
                return false;
            }
        }

        let attachmentFiles = payload?.attachmentFiles;
        let listItemUrls = attachmentFiles?.map((item) => item?.fileUri);
        let fileListSuccess = [];
        let suffixOfAttachments = {};
        const results = await downloadAndUploadFilesFromUrls(listItemUrls, organizationId, attachmentFiles);

        if (!Utils.isEmptyObject(results)) {
            fileListSuccess = !Utils.isEmptyArray(results?.fileListSuccess)
                ? results?.fileListSuccess
                : [];
            suffixOfAttachments = !Utils.isEmptyObject(results?.suffixOfAttachments)
                ? results?.suffixOfAttachments
                : {};
        }

        // Get userId from context
        const userId = sdeskContext?.userId || localStorageUtil.getUserDetailId() || '';

        let createTicketRequestDto = {
            ticketType: Constant.TICKET_TYPE.KNOWLEDGE,
            outline: payload.outline || '',
            corporationOrigin: organizationId,
            createUserId: userId,
            projectOrigin: projectId,
            projectTarget: [projectId],
            corporationTarget: organizationId,
            affiliationOrganization: organizationId,
            affiliationProject: projectId,
            type: kboardKnowledgeForm.id,
            status: '',
            fields: JSON.stringify(customFields),
            customer: Constant.NA,
            public: Constant.TICKET_PUBLIC.PUBLIC,
            viewPermission: [
                Constant.KNOWLEDGE_VIEW_EDIT_PERMISSION.CREATOR_ONLY,
                Constant.KNOWLEDGE_VIEW_EDIT_PERMISSION.IN_PROJECT,
            ],
            editPermission: [
                Constant.KNOWLEDGE_VIEW_EDIT_PERMISSION.CREATOR_ONLY,
                Constant.KNOWLEDGE_VIEW_EDIT_PERMISSION.PROJECT_MANAGER,
            ],
            additionalViewPermission: JSON.stringify(
                Constant.KNOWLEDGE_ADDITIONAL_PERMISSION_TEMPLATE,
            ),
            additionalEditPermission: JSON.stringify(
                Constant.KNOWLEDGE_ADDITIONAL_PERMISSION_TEMPLATE,
            ),
            chatPermission: Constant.KNOWLEDGE_CHAT_PERMISSION.IN_PROJECT,
            fileListSuccess: fileListSuccess,
            suffixOfAttachments: JSON.stringify(suffixOfAttachments),
            sessionId: localStorageUtil.getSessionBrowserId() || '',
            isServicesPDFOfCorpOn: isDisplaySplitPDF(
                corporationDetail,
                Constant.FORM_TYPE.FORM_KNOWLEDGE,
            ),
            isSendDraft: false,
            fileUploadedList: [],
            oldDraftTicketId: '',
            fileUploadedDeleted: [],
        };

        // Call Lambda to create new knowledge ticket
        const createResult = await createNewTicketAPI(null, { createTicketRequestDto });

        // Send AI Chat if payload contains aiPrompt
        if (payload?.aiPrompt?.message && createResult?.id) {
            await sendTicketInfoToKboard(createResult?.id, sheetInfo.sheetId);
            await uploadSnapshotXML(payload,{corporationId: organizationId, projectId: projectId, ticketId: createResult?.id})
            await sendAIChatFromKboard({
                payload,
                handleConnectData,
                knowledgeTicketId: createResult.id,
                sdeskContext,
            });
        }

        return createResult;
    } catch (error) {
        console.log('[KBoardConnectHandler] handleDataFromKboard error:', error);
    }
};

/**
 * Update an existing KB Knowledge ticket with new data from KBoard.
 * Handles field updates and attachment file replacement (delete old + add new).
 *
 * @param {Object} params
 * @returns {Promise<Object|null>}
 */
const updateExistingKBKnowledge = async ({
    ticketId,
    ticketAllData,
    customFields,
    payload,
    organizationId,
    sdeskContext,
    kboardKnowledgeForm,
    kbTimestampField,
    userMail,
    corporationDetail,
}) => {
    try {
        const userId = sdeskContext?.userId || localStorageUtil.getUserDetailId() || '';
        // Get current ticket field data from ticketAllData
        const ticketFieldData = ticketAllData?.filter((item) => item.dataType === 'FIELD') || [];

        // Get current ticket main record for permission data
        let ticketMainRecord = ticketAllData?.find((item) => item.dataType === 'TICKET') || {};
        ticketMainRecord = Utils.convertJSONDataToObject(ticketMainRecord);

        // Restore ticket if it was soft deleted
        if (!Utils.isEmptyString(ticketMainRecord.deletedAt)) {
            await editTicketData(null, {
                ticketId: ticketId,
                currentUserId: userId,
                currentUserMail: userMail || '',
                corporationId: organizationId,
                updateMethod: Constant.UPDATE_TICKET_METHOD.UPDATE_DELETED_AT,
                ticketType: Constant.TICKET_TYPE.KNOWLEDGE,
                deletedAt: null,
            });
        }

        // Early return: if kbTimestamp field has not changed, skip update entirely
        if (kbTimestampField?.id) {
            const timestampRecord = ticketFieldData.find((f) => f.dataID === kbTimestampField.id);
            const currentTimestamp = timestampRecord?.dataValue?.value;
            const newTimestamp = customFields[kbTimestampField.id] || '';
            if (currentTimestamp === newTimestamp) {
                return null;
            }
        }

        // Build editedFieldValue from new customFields
        const editedFieldValue = {};
        const listFieldEdited = [];
        const mapDataIdToId = {};
        // Compare new values vs existing, only update changed fields
        for (const [fieldId, newValue] of Object.entries(customFields)) {
            const existingField = ticketFieldData.find((f) => f.dataID === fieldId);
            if (!existingField) continue;
            const currentValue = existingField?.dataValue?.value || '';
            if (currentValue !== newValue) {
                editedFieldValue[existingField.id] = JSON.stringify({ value: newValue });
                listFieldEdited.push(fieldId);
                mapDataIdToId[fieldId] = existingField.id;
            }
        }

        // --- ATTACHMENT HANDLING ---
        // 1. Get current attachments from ticket
        const attachmentRecord = ticketFieldData.find((f) => {
            return f.dataID === Constant.TICKET_DATA_ID.ATTACHMENT;
        });

        let listFileDelete = [];
        let newFileList = [];
        const currentAttachments = attachmentRecord
            ? Utils.convertJSONDataToObject(attachmentRecord.dataValue)?.value || []
            : [];
        // 2. Compare originUrl of current attachments vs new URLs from KBoard
        const attachmentFiles = payload?.attachmentFiles || [];
        const newOriginUrls = attachmentFiles.map((item) => item?.fileUri).filter(Boolean);
        const currentOriginUrls = currentAttachments.map((file) => file.originUrl).filter(Boolean);
        // Files to delete: exist in current but not in new list (by originUrl)
        listFileDelete = currentAttachments
            .filter((file) => file.originUrl && !newOriginUrls.includes(file.originUrl))
            .map((file) => ({
                fileUrl: file.fileUrl,
                fileSize: file.fileSize || '0',
            }));

        // Files to add: exist in new list but not in current (by originUrl)
        const urlsToAdd = newOriginUrls.filter((url) => !currentOriginUrls.includes(url));

        // 3. Upload only new files from KBoard
        if (!Utils.isEmptyArray(urlsToAdd)) {
            const results = await downloadAndUploadFilesFromUrls(urlsToAdd, organizationId, attachmentFiles);
            if (!Utils.isEmptyObject(results)) {
                newFileList = results?.fileListSuccess || [];
            }
        }

        // 4. Add attachment and suffixOfAttachments record IDs to mapDataIdToId
        if (attachmentRecord?.id) {
            mapDataIdToId['attachment'] = attachmentRecord.id;
        }
        const suffixRecord = ticketFieldData.find(
            (f) => f.dataID === Constant.TICKET_DATA_ID.SUFFIX_OF_ATTACHMENTS,
        );
        if (suffixRecord?.id) {
            mapDataIdToId['suffixOfAttachments'] = suffixRecord.id;
        }

        // Skip if no field changes and no file changes
        if (
            listFieldEdited.length === 0 &&
            newFileList.length === 0 &&
            listFileDelete.length === 0
        ) {
            return null;
        }

        const updateTicketRequestDto = {
            currentUserMail: userMail,
            ticketId: ticketId,
            corporationId: organizationId,
            ticketType: Constant.TICKET_TYPE.KNOWLEDGE,
            currentUserId: userId,
            updateMethod: Constant.UPDATE_TICKET_METHOD.UPDATE_FIELD_TICKET,
            mapDataIdToId: JSON.stringify(mapDataIdToId),
            editedFieldValue: JSON.stringify(editedFieldValue),
            listFieldEdited: JSON.stringify(listFieldEdited),
            updateAttachFile: JSON.stringify({
                newFileList: newFileList,
                listFileDelete: listFileDelete,
                listFilePdfUpdated: [],
            }),
            formId: kboardKnowledgeForm.id,
            isHelpDesk: false,
            isNotiToCustomerDone: true,
            isNotiToHelpDeskDone: true,
            viewPermission: ticketMainRecord.viewPermission || [],
            editPermission: ticketMainRecord.editPermission || [],
            additionalViewPermission: JSON.stringify(ticketMainRecord.additionalViewPermission),
            additionalEditPermission: JSON.stringify(ticketMainRecord.additionalEditPermission),
            chatPermission: ticketMainRecord.chatPermission || '',
            sessionId: localStorageUtil.getSessionBrowserId() || '',
            isServicesPDFOfCorpOn: isDisplaySplitPDF(
                corporationDetail,
                Constant.FORM_TYPE.FORM_KNOWLEDGE,
            ),
        };

        const updateResult = await editTicketData(null, updateTicketRequestDto);
        await uploadSnapshotXML(payload,{corporationId: organizationId, projectId: ticketMainRecord.affiliationProject, ticketId: ticketId})

        return updateResult;
    } catch (error) {
        console.log('[KBoardConnectHandler] updateExistingKBKnowledge error:', error);
    }
};

/**
 * Check if a KB Knowledge ticket already exists for given projectId and sheetId.
 * @param {string} projectId - The project ID to check
 * @param {string} sheetId - The kboard sheet ID to check
 * @param {string} corporationId - The corporation ID (used as OpenSearch index prefix)
 * @returns {Promise<{exists: boolean, ticketId: string|null, totalHits: number}>}
 */
export const checkKBKnowledgeExists = async (projectId, sheetId, corporationId) => {
    if (!projectId || !sheetId || !corporationId) {
        return { exists: false, ticketId: null, totalHits: 0 };
    }

    const result = await getPublicAPI(null, {
        getApiType: Constant.GET_API_PUBLIC_TYPE.CHECK_KB_KNOWLEDGE_EXISTS,
        projectId,
        sheetId,
        corporationId
    }, Constant.AUTH_MODE_USER_POOLS);

    if (result?.success) {
        const data = Utils.convertJSONDataToObject(result.payload);
        return { success: true, ...data} || { success: true, exists: false, ticketId: null, totalHits: 0 };
    }

    return {success: false, exists: false, ticketId: null, totalHits: 0 };
};

/**
 * Send AI Chat message from KBoard flow.
 * Pushes mock messages (user msg + loading) into the chat thread, then fires the sendAIChat mutation.
 *
 * @param {Object} params
 * @param {Object} params.payload - KBoard payload containing aiPrompt info
 * @param {Object} params.handleConnectData - Context data from InputChatAiGroupButtons
 * @param {string} params.knowledgeTicketId - ID of the kboard knowledge ticket (created or existing)
 * @param {Object} params.sdeskContext - { organizationId, projectId, userId }
 */
export const sendAIChatFromKboard = async ({ payload, handleConnectData, knowledgeTicketId, sdeskContext }) => {
    try {
        let aiPrompt = Utils.isEmptyObject(payload?.aiPrompt) ? {} : {...payload?.aiPrompt} ;
        const message = aiPrompt?.message;

        if (aiPrompt?.chatGptModel === Constant.NO_PREFERENCE) {
            aiPrompt.chatGptModel = '';
            aiPrompt.reasoningEffort = '';
        }

        if (Utils.isEmptyString(message)) {
            return null;
        }

        const {
            chatThreadId,
            usingChatGptModel,
            usingReasoningEffort,
            filterKnowledgeListData,
            setListMessageContent,
            setIsEnableBtnSend,
            setIsShowButtonStop,
            setIsOnlyRunChatAI,
            setIsStopStreamContent,
            setIdOfResponseMessage,
            setIsDisabledInputChat,
            setUsingChatGptModel,
            setUsingReasoningEffort
        } = handleConnectData;

        let chatGptModel = '';
        let reasoningEffort = '';
        if (!Utils.isEmptyString(aiPrompt?.chatGptModel)) {
            chatGptModel = aiPrompt?.chatGptModel;
            reasoningEffort = aiPrompt.reasoningEffort
                ? aiPrompt.reasoningEffort
                : OpenAiUtil.defaultReasoningEffort(chatGptModel, false);
        } else {
            chatGptModel = usingChatGptModel || '';
            reasoningEffort = usingReasoningEffort || '';
        }

        setUsingChatGptModel?.(chatGptModel);
        setUsingReasoningEffort?.(reasoningEffort);
        
        const corporationId = sdeskContext?.organizationId || '';
        const projectId = sdeskContext?.projectId || '';
        const currentUserId = sdeskContext?.userId || localStorageUtil.getUserDetailId() || '';
        const currentUserDetail = handleConnectData?.currentUserDetail;

        // Build view/drilldown search params from filterKnowledgeListData
        const viewId = filterKnowledgeListData?.viewSelectedId || '';
        const visibilitySettingType =
            filterKnowledgeListData?.viewStructure?.visibility ||
            Constant.VISIBILITY_SETTING_TYPE.CUSTOMER;
        const ticketProperty = filterKnowledgeListData?.viewStructure?.ticketProperty || '';
        const projectIdKnowledgeSelected =
            filterKnowledgeListData?.projectIdKnowledgeSelected || projectId;
        const ticketCreatedProjectId = filterKnowledgeListData?.ticketCreatedProjectId || projectId;
        const ticketCreatedCorporationId =
            filterKnowledgeListData?.ticketCreatedCorporationId || corporationId;

        const chatPostId = uuidv4();

        // Upload KBoard screen capture if available
        const kboardScreenCapture = await uploadKboardScreenCapture(payload, {
            corporationId,
            projectId,
            chatThreadId
        });

        // Get kboard knowledge ticket shareLinks data from backend
        let composedMessage = message;
        let shareLinksForDto = Utils.getShareLinkUrls(message);

        if (!Utils.isEmptyString(knowledgeTicketId)) {
            // Retry when backend signals attachmentsPending (AttachFile records not yet created)
            const MAX_COMPOSE_RETRY = 3;
            const COMPOSE_RETRY_DELAY_MS = 1500;
            let composedParsed = null;

            for (let attempt = 0; attempt < MAX_COMPOSE_RETRY; attempt++) {
                const kbData = await getPublicAPI(
                    null,
                    {
                        getApiType: Constant.GET_API_PUBLIC_TYPE.COMPOSE_KBOARD_MESSAGE,
                        kboardKnowledgeTicketId: knowledgeTicketId
                    },
                    Constant.AUTH_MODE_USER_POOLS
                );

                if (!kbData?.success) break;

                const parsed = Utils.convertJSONDataToObject(kbData.payload);

                if (parsed?.attachmentsPending && attempt < MAX_COMPOSE_RETRY - 1) {
                    await new Promise(resolve => setTimeout(resolve, COMPOSE_RETRY_DELAY_MS));
                    continue;
                }

                composedParsed = parsed;
                break;
            }

            if (composedParsed) {
                let ticketShareLinks = [];
                let attachFileShareLinks = [];
                if (!Utils.isEmptyString(composedParsed?.ticketShareLink)) {
                    ticketShareLinks.push(composedParsed.ticketShareLink);
                }

                if (!Utils.isEmptyArray(composedParsed?.imageAttachments)) {
                    composedParsed.imageAttachments.forEach(attach => {
                        if (!Utils.isEmptyString(attach?.shareLink)) {
                            attachFileShareLinks.push(attach.shareLink);
                        }
                    });
                }

                // Compose message: message + [wrapped XML if case 2] + shareLinks
                const parts = [message];
                const selectionXml = payload?.selectionXmlData?.xml || '';
                if (!Utils.isEmptyString(selectionXml)) {
                    parts.push(
                        `[SELECTION_KBOARD_XML_START]${selectionXml}[SELECTION_KBOARD_XML_END]`
                    );
                }

                if (ticketShareLinks.length > 0 || attachFileShareLinks.length > 0) {
                    parts.push('参照ナレッジ： ', ...ticketShareLinks);
                    shareLinksForDto = [...shareLinksForDto, ...ticketShareLinks];
                    if (!Utils.isEmptyArray(attachFileShareLinks) && payload?.includeImage) {
                        parts.push('添付画像：', ...attachFileShareLinks);
                        shareLinksForDto = [...shareLinksForDto, ...attachFileShareLinks];
                    }
                }
                composedMessage = parts.join('\n');
            }
        }

        // Build display content (strip XML markers for user view)
        const displayContent = composedMessage.replace(
            /\[SELECTION_KBOARD_XML_START\][\s\S]*?\[SELECTION_KBOARD_XML_END\]\n?/g,
            ''
        );

        let creatorUserName = Utils.getFullNameOfUser(Constant.LANGUAGE_TYPE.JP, currentUserDetail);
        // Build mock attachments for image display
        const mockAttachments = [];
        if (!Utils.isEmptyString(kboardScreenCapture?.s3Key)) {
            mockAttachments.push({
                id: `kboard-capture-${Date.now()}`,
                s3Path: kboardScreenCapture.s3Key,
                localUrl: kboardScreenCapture.localUrl
            });
        }
        // Push mock messages into chat thread (user message + loading)
        const mockMessages = [
            {
                isFromMe: true,
                user: creatorUserName,
                dateTime: Utils.getDateTimeByFormatJapanYYYY_MM_DDHHMM(new Date()),
                content: displayContent,
                postType:
                    mockAttachments.length > 0 ? Constant.POST_TYPE.IMAGE : Constant.POST_TYPE.MSG,
                isMockMsg: true,
                attachments: mockAttachments
            },
            {
                isFromMe: false,
                isLoadingMessage: true,
                idOfLoadingMessage: chatPostId
            }
        ];

        setListMessageContent?.(prev => [...prev, ...mockMessages]);
        setIsEnableBtnSend?.(false);
        setIsShowButtonStop?.(true);
        setIsOnlyRunChatAI?.(true);
        setIsStopStreamContent?.('');
        setIdOfResponseMessage?.(chatPostId);

        // Update tab name if this is the first message in the thread
        const { setPanelList, panelId, tabId, listMessageContent } = handleConnectData;
        if (Utils.isEmptyArray(listMessageContent) && setPanelList && panelId && tabId) {
            updateTabNameAfterSendMessage(displayContent, panelId, setPanelList, tabId);
        }
        // Build sendAIChat request DTO
        const dto = {
            corporationId,
            projectId: projectIdKnowledgeSelected,
            currentUserId,
            chatThreadId,
            message: composedMessage,
            basePrompt: '',
            usingBasicPrompt: false,
            visibilitySettingType: visibilitySettingType,
            visibilitySetting: 'new_arrived_knowledge',
            publicRange: '',
            projectInuse: projectId,
            userAffiliationOrganization: corporationId,
            projectInuseType: '',
            viewId: viewId,
            optionSearch: {},
            drillDownSearch: {},
            ticketIdList: [],
            ticketProperty: ticketProperty,
            resetSearchCondition: !!filterKnowledgeListData?.resetSearchCondition,
            from: 0,
            size: 30,
            logKibanaData: JSON.stringify({}),
            chatPostId,
            listUrlShareLink: shareLinksForDto,
            currentCorporationId: corporationId,
            ticketCreatedProjectId: ticketCreatedProjectId,
            ticketCreatedCorporationId: ticketCreatedCorporationId,
            chatGptModel,
            attachments: JSON.stringify([]),
            fileAttachments: JSON.stringify([]),
            selectedMcpServers: [],
            reasoningEffort,
            kboardScreenCapture: kboardScreenCapture.s3Key,
        };


        // Fire and forget — response handled by subscription
        sendAIChatMessage(null, dto, setListMessageContent, setIsDisabledInputChat);

        return { chatPostId };
    } catch (error) {
        console.log('[KBoardConnectHandler] sendAIChatFromKboard error:', error);
    }
};

/**
 * Lock mechanism for routeKboardResult to prevent multiple concurrent tab creations.
 * When a new tab is being created (Case 3), subsequent calls wait and reuse the same thread.
 */
let _routeLock = {
    isCreating: false,
    waitQueue: [],
};

/**
 * Wait for the current tab creation to complete.
 * @returns {Promise<string>} The newly created thread ID (or empty on failure)
 */
const _waitForTabCreation = () => {
    return new Promise((resolve) => {
        _routeLock.waitQueue.push(resolve);
    });
};

/**
 * Release all waiting resolvers with the created thread ID.
 * @param {string} threadId - The created thread ID (or empty string on failure)
 */
const _releaseWaitQueue = (threadId) => {
    const queue = _routeLock.waitQueue;
    _routeLock.waitQueue = [];
    _routeLock.isCreating = false;
    queue.forEach(resolve => resolve(threadId));
};

/**
 * Helper to push an item into the kboardPendingResultAtom queue.
 */
const _pushToPendingQueue = ({ targetThreadId, payload, staticData }) => {
    const currentQueue = getRecoil(kboardPendingResultAtom) || [];
    setRecoil(kboardPendingResultAtom, [...currentQueue, { targetThreadId, payload, staticData }]);
};

/**
 * Route kboard result to the correct ChatAI tab instance.
 * Uses recoil-nexus to read current panel state and decide:
 *   Case 1: target thread is active → push to pending queue
 *   Case 2: target thread exists in tabList but not active → switch tab + push to queue
 *   Case 3: target thread not in tabList → create new tab/thread + push to queue
 *
 * Lock mechanism ensures that when multiple messages arrive simultaneously after tab close,
 * only ONE new tab is created and subsequent messages reuse the same thread.
 *
 * @param {Object} payload - Raw kboard payload (from KBOARD_SEND_RESULT)
 * @param {Object} handleConnectData - Context data captured at connect time
 */
export const routeKboardResult = async (payload, handleConnectData) => {
    try {
        const targetThreadId = payload?.sdeskContext?.aiThreadChatId || handleConnectData?.chatThreadId || '';
        if (Utils.isEmptyString(targetThreadId)) {
            return;
        }

        // Read current panel state via recoil-nexus
        const panelList = getRecoil(multiPanelAtom) || [];
        const chatAiPanel = panelList.find(p => p.type === Constant.PANEL_TYPE.OPEN_CHAT_AI);

        // Guard: panel must exist and be displayed
        if (!chatAiPanel || !chatAiPanel.isDisplay) {
            return;
        }

        // Build staticData (serializable context — no React setters)
        const latestCorporationDetail = getRecoil(corporationAtom) || {};
        const { corporationSetting, limitValue, inviterSetting, contractType } = latestCorporationDetail;
        const isGuestContract = [
            Constant.CORPORATION_CONTRACT_TYPE.GUEST,
            Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK
        ].includes(contractType);
        const latestChatGptModel = isGuestContract
            ? Utils.convertJSONDataToObject(inviterSetting)?.chatGPTModel
            : Utils.convertJSONDataToObject(corporationSetting)?.chatGPTModel
            || Utils.convertJSONDataToObject(limitValue)?.chatGPTModel
            || '';
        const latestReasoningEffort = OpenAiUtil.defaultReasoningEffort(latestChatGptModel);

        const staticData = {
            usingChatGptModel: latestChatGptModel || handleConnectData?.usingChatGptModel || '',
            usingReasoningEffort: latestReasoningEffort || handleConnectData?.usingReasoningEffort || '',
            formList: handleConnectData?.formList || [],
            fieldList: handleConnectData?.fieldList || [],
            filterKnowledgeListData: handleConnectData?.filterKnowledgeListData || {},
            userMail: handleConnectData?.userMail || '',
            corporationDetail: latestCorporationDetail,
            API: handleConnectData?.API || null,
            enqueueSnackbar: handleConnectData?.enqueueSnackbar || null,
        };

        const tabList = chatAiPanel.tabList || [];
        const activeTabIndex = chatAiPanel.activeTab || 0;
        const activeTab = tabList[activeTabIndex];
        const activeThreadId = activeTab?.chatThreadId || '';

        // Case 1: target thread is the active tab
        if (activeThreadId === targetThreadId) {
            _pushToPendingQueue({ targetThreadId, payload, staticData });
            return;
        }

        // Case 2: target thread exists in tabList but not active
        const targetTabIndex = tabList.findIndex(tab => tab.chatThreadId === targetThreadId);
        if (targetTabIndex >= 0) {
            setRecoil(updatePanelSelector, {
                panelType: Constant.PANEL_TYPE.OPEN_CHAT_AI,
                activeTab: targetTabIndex,
            });
            _pushToPendingQueue({ targetThreadId, payload, staticData });
            return;
        }

        // Case 3: target thread not in tabList → create new tab
        // Check if another call is already creating a tab — wait and reuse that thread
        if (_routeLock.isCreating) {
            const existingNewThreadId = await _waitForTabCreation();
            if (!Utils.isEmptyString(existingNewThreadId)) {
                _pushToPendingQueue({ targetThreadId: existingNewThreadId, payload, staticData });
            }
            return;
        }

        // Acquire lock
        _routeLock.isCreating = true;

        const userDetail = getRecoil(userAtom) || {};
        const corporationDetail = getRecoil(corporationAtom) || {};
        const projectInuseDetail = getRecoil(projectInuseDetailAtom) || {};
        const userPermission = getRecoil(userPermissionToViewKnowledgeAtom) || {};

        // Check tab limit
        const isLimited = await isLimitedTabsLength({
            panelDataChatAI: chatAiPanel,
            projectInuseDetail,
            userDetail,
        });

        if (isLimited) {
            const { enqueueSnackbar } = staticData;
            if (enqueueSnackbar) {
                enqueueSnackbar(buildSnackbarMessage('MSG-CM-WARNING-LIMIT-TAB'), {
                    variant: 'error',
                    autoHideDuration: 3000,
                    style: { whiteSpace: 'pre-line' },
                });
            }
            _releaseWaitQueue('');
            return;
        }

        // Create new tab/thread via handleOpenPanelOpenChatAI
        const addNewPanel = (data) => setRecoil(setPanelSelector, data);
        const newThreadId = await handleOpenPanelOpenChatAI(
            staticData.API,
            panelList,
            userDetail,
            corporationDetail,
            projectInuseDetail,
            addNewPanel,
            userPermission,
            { API: staticData.API },
            () => {},
        );

        if (!Utils.isEmptyString(newThreadId)) {
            // Notify kboard about the new thread ID so subsequent messages use it
            sendUpdatedThreadIdToKboard(newThreadId);

            // Push pending result for the newly created ChatAI instance
            _pushToPendingQueue({ targetThreadId: newThreadId, payload, staticData });
        }

        // Release lock — notify waiting messages with the new thread ID
        _releaseWaitQueue(newThreadId || '');
    } catch (error) {
        console.log('[KBoardConnectHandler] routeKboardResult error:', error);
        // Ensure lock is released on error to avoid deadlock
        if (_routeLock.isCreating) {
            _releaseWaitQueue('');
        }
    }
};
